import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useApiClient } from "@/shared/api/ApiClientProvider";
import { ApiError } from "@/shared/api/httpClient";
import { clearLegacySessionCredential } from "@/shared/api/sessionStorage";
import type { AppBootstrap } from "@/shared/types/app";

type BootstrapStatus = "loading" | "ready" | "error";

interface BootstrapContextValue {
  status: BootstrapStatus;
  data: AppBootstrap | null;
  error: Error | null;
  markOnboardingComplete: () => void;
  markReferencesSubmitted: () => void;
  refresh: () => Promise<AppBootstrap | null>;
}

const BootstrapContext = createContext<BootstrapContextValue | null>(null);

const EMPTY_BOOTSTRAP: AppBootstrap = { session: null, profile: null };

export function BootstrapProvider({ children }: { children: ReactNode }) {
  const apiClient = useApiClient();
  // HttpOnly cookies are intentionally invisible to JavaScript, so bootstrap
  // always asks /auth/me whether a browser session exists.
  const [status, setStatus] = useState<BootstrapStatus>("loading");
  const [data, setData] = useState<AppBootstrap | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const dataRef = useRef(data);
  dataRef.current = data;

  const refresh = useCallback(async (): Promise<AppBootstrap | null> => {
    // Soft mid-session refreshes (Launch, verify-email) must not flip to
    // "loading"/"error" when we already have a session - AuthProvider clears
    // the user on bootstrap error and that logged people out after a successful
    // onboarding complete.
    const keepAlive = Boolean(dataRef.current?.session);
    if (!keepAlive) {
      setStatus("loading");
    }
    setError(null);
    try {
      const next = await apiClient.getBootstrap();
      setData(next);
      setStatus("ready");
      // Warm Matches chunk while /me is already in flight / just finished —
      // email deep links to /matches?match=… feel snappier.
      if (next.session) {
        void import("@/pages/MatchesPage");
      }
      return next;
    } catch (caught) {
      const err =
        caught instanceof Error ? caught : new Error("Unable to bootstrap app");
      const unauthorized = caught instanceof ApiError && caught.status === 401;
      setError(err);
      if (unauthorized || !keepAlive) {
        setStatus("error");
        if (unauthorized) {
          setData(EMPTY_BOOTSTRAP);
        }
        return null;
      }
      // Transient network/5xx with an existing session: stay ready on prior data.
      setStatus("ready");
      return dataRef.current;
    }
  }, [apiClient]);

  const markOnboardingComplete = useCallback(() => {
    setData((current) =>
      current?.profile
        ? {
            ...current,
            profile: {
              ...current.profile,
              onboardingStatus: "complete",
              hasSubmittedReferences: current.profile.hasSubmittedReferences ?? false,
            },
          }
        : current,
    );
  }, []);

  const markReferencesSubmitted = useCallback(() => {
    setData((current) =>
      current?.profile
        ? {
            ...current,
            profile: {
              ...current.profile,
              hasSubmittedReferences: true,
            },
          }
        : current,
    );
  }, []);

  useEffect(() => {
    clearLegacySessionCredential();
    void refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      status,
      data,
      error,
      markOnboardingComplete,
      markReferencesSubmitted,
      refresh,
    }),
    [status, data, error, markOnboardingComplete, markReferencesSubmitted, refresh],
  );

  return <BootstrapContext.Provider value={value}>{children}</BootstrapContext.Provider>;
}

export function useBootstrap() {
  const context = useContext(BootstrapContext);
  if (!context) {
    throw new Error("useBootstrap must be used inside BootstrapProvider");
  }
  return context;
}
